import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router";
import { useLTIP } from "@hooks/useLTIP";
import Grid from "./RSUGrid";
import { useStockPrice } from "@hooks/useStockPrice";
import CustomButton from "@components/CustomButton";
import NumberBoxWrapper from "@/components/NumberBoxWrapper";
import NoDataMessage from "@/components/NoDataMessage";
import withDisclaimer from "@/hoc/withDisclaimer";
import HouseholdClientTabs from "@/components/HouseholdClientTabs";
import StockPriceTooltip from "@/components/StockPriceTooltip";
import { LTIPType } from "@/types/enums";
import useHeaderChange from "@/hooks/useHeaderChange";
import { SarSkeleton } from "../SAR/SarSkeleton";
import useSelectedClient from "@/hooks/useSelectedClient";
import useDefaultSelectedClient from "@/hooks/useDefaultSelectedClient";
import { checkClientsBirthdates, getOwnerName } from "@/helper/helper";
import { NoContactBirthDateMessage } from "../HouseholdContact";

function RestrictedStockAwards() {
  const { id } = useParams();
  const { household } = useHeaderChange(id as string, "Restricted Stock Awards");
  const [stockPrice, setStockPrice] = useState(0);
  const {
    data,
    isSuccess: rsuLoaded,
    isLoading: isRsuLoading,
    isError,
    isRefetching,
  } = useLTIP(id as string, LTIPType.RSU, null, household);
  const { selectedClient, setSelectedClient } = useSelectedClient(household, LTIPType.RSU);
  useDefaultSelectedClient(household, data, selectedClient, setSelectedClient, LTIPType.RSU);

  const rsuForClient = useMemo(() => {
    if (rsuLoaded && selectedClient) {
      const clientRsu = data?.data?.find((item) => item.ownerId === selectedClient);
      return { ...clientRsu, price: stockPrice };
    } else return undefined;
  }, [rsuLoaded, selectedClient, stockPrice]);

  const { data: stockInfo, isSuccess: stockInfoFetched } = useStockPrice(rsuForClient?.ticker);

  useEffect(() => {
    if (stockInfoFetched) setStockPrice(stockInfo?.data?.value ?? 0);
  }, [selectedClient, stockInfoFetched]);

  if (isRsuLoading || isRefetching) return <SarSkeleton />;

  if (checkClientsBirthdates(household)) {
    return <NoContactBirthDateMessage household={household} />;
  }

  return (
    <div className={`flex flex-col text-neutral-100 ${rsuForClient ? "gap-4" : ""}`}>
      <div id="rsu-combined-values" className="w-1/2" />
      <div className="py-2" />
      <h1 className="text-lg font-medium">
        {rsuForClient?.name ?? ""}{" "}
        {!data?.bothClientsHaveData && !household?.isSingle
          ? `for ${getOwnerName(household, selectedClient)}`
          : ""}{" "}
      </h1>
      <div className="flex items-center justify-between">
        {household?.secondaryContact && data?.bothClientsHaveData ? (
          <HouseholdClientTabs
            household={household}
            selectedClientExternalCode={selectedClient}
            setSelectedClientExternalCode={setSelectedClient}
          />
        ) : null}
        <div className="z-40 flex gap-6">
          {stockInfo?.data?.value !== stockPrice && stockInfoFetched ? (
            <CustomButton
              text="Reset"
              variant="primary"
              onClickHandler={() => {
                setStockPrice(stockInfo?.data?.value ?? 0);
              }}
            />
          ) : null}
          {stockInfoFetched && (stockInfo.hasError || stockInfo?.hasWarning) ? (
            <StockPriceTooltip
              ticker={rsuForClient?.ticker}
              timestamp={stockInfo?.data?.timestamp}
            />
          ) : null}
          {!rsuForClient?.ticker && rsuLoaded ? (
            <div className="w-[148px] text-center">No Ticker</div>
          ) : !rsuForClient?.externalCode ? null : (
            <NumberBoxWrapper
              format={"$ #,##0.00"}
              stylingMode="outlined"
              labelMode="floating"
              width={148}
              label={`${rsuForClient?.ticker} Stock Price`}
              value={stockPrice ?? 0}
              onValueChange={(val) => {
                setStockPrice(val);
              }}
            />
          )}
        </div>
      </div>
      {isError ? <div>Failed fetching RSU data</div> : null}
      {!rsuForClient?.externalCode && rsuLoaded && !isRsuLoading ? (
        <NoDataMessage />
      ) : (
        <Grid rsu={rsuForClient} stockPrice={stockPrice} />
      )}
    </div>
  );
}

const rsuWithDisclaimer = withDisclaimer(RestrictedStockAwards);

export default rsuWithDisclaimer;
